import React, { useContext, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { VisibilityOutlined, VisibilityOffOutlined } from '@material-ui/icons';
import Button from 'screens/Integrations/components/IntegrationButton';
import LoadingSpinner from 'screens/Integrations/components/IntegrationSpinner';
import { ChevronDown, ChevronUp } from 'syngenta-digital-cropwise-react-ui-kit';
import Tag from 'screens/Integrations/components/IntegrationTag';

import FieldMapView from 'components/FieldMapView';
import { fieldToFeature } from 'screens/Property/PropertiesLanding/ImportPropertyModal/fieldImporterHelper';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import SelectWithSearch from 'screens/Integrations/components/SelectWithSearch';

import { PropertiesMatchContext } from '../../context/PropertiesMatchProvider';
import ClickableDiv from '../../../../../components/ClickableDiv';
import MatchArrowIcon from '../FarmList/components/icons/MatchArrowIcon';
import VendorPropertyMapView from './components/VendorPropertyMapView';

const propertyViewDefaultPropProperty = {};
const PropertyView = ({
  property = propertyViewDefaultPropProperty,
  parentName = '',
  integrationName = ''
}) => {
  const {
    addPropertyMatchId,
    fetchVendorProperties,
    fetchMatches,
    isLoading,
    state: { vendorProperties, matches }
  } = useContext(PropertiesMatchContext);

  const [selectedProperty, setSelectedProperty] = useState(0);

  const [selectedVendorPropertyId, setSelectedVendorPropertyId] = useState(
    null
  );

  const [displayVendorBoundary, setDisplayVendorBoundary] = useState(false);

  const isPropertySelected = prpt => {
    if (!selectedProperty) {
      return false;
    }
    return prpt.id === selectedProperty.id;
  };

  const openProperty = () => {
    const currentIsOpen = selectedProperty?.id === property.id;
    setSelectedProperty(currentIsOpen ? null : property);
  };

  const onChangeVendorProperty = value => {
    if (value !== selectedVendorPropertyId) {
      setDisplayVendorBoundary(false);
      setSelectedVendorPropertyId(value);
      addPropertyMatchId(selectedProperty.id, value);
    }
  };

  const handleVendorBoundary = () => {
    setDisplayVendorBoundary(prev => !prev);
  };

  useEffect(() => {
    fetchVendorProperties();
    fetchMatches();
    setSelectedVendorPropertyId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const match = matches.find(m => property.id === m.cwfId);
    if (!match) {
      setSelectedVendorPropertyId(null);
    } else if (match && match.vendorId !== selectedVendorPropertyId) {
      setSelectedVendorPropertyId(match.vendorId);
    }
  }, [matches, selectedVendorPropertyId, property?.id]);

  const options = useMemo(() => {
    return vendorProperties?.map(vendorProperty => {
      const label =
        integrationName === INTEGRATIONS.agrian
          ? vendorProperty.name
          : `${vendorProperty.fieldName} - ${vendorProperty.name}`;

      return {
        label,
        title: label,
        value: vendorProperty.id
      };
    });
  }, [vendorProperties, integrationName]);

  const feature = fieldToFeature(property);
  return (
    <div
      data-testid="property-collapse"
      className="text-l rounded-lg p-4 shadow-md mt-1 grid-flow-row auto-rows-max mb-5"
    >
      <ClickableDiv
        className="flex bg-transparent text-info-dark"
        onClick={openProperty}
      >
        <div className="w-20" data-testid="property-match-status">
          {selectedVendorPropertyId ? (
            <Tag key="matching-state-property" color="#C3EAD1">
              <span className="justify-self-center w-full text-xs mr-5 text-green-900 ml-2">
                Matched
              </span>
            </Tag>
          ) : (
            <Tag key="matching-state-property" color="#FFE4AE">
              <span className="justify-self-center w-full text-xs mr-5 text-yellow-700">
                Unmatched
              </span>
            </Tag>
          )}
        </div>
        <div className="flex justify-start ml-2" data-testid="property-title">
          {parentName && <span>{parentName} /</span>}
          <span className="font-semibold">&nbsp;{property?.name}</span>
        </div>
        <div className="flex justify-end flex-1">
          {isPropertySelected(property) ? (
            <ChevronUp fontSize="small" className="text-neutral-600" />
          ) : (
            <ChevronDown fontSize="small" className="text-neutral-600" />
          )}
        </div>
      </ClickableDiv>

      {isPropertySelected(property) && (
        <div className="justify-self-start pr-15 col-span-w w-full mt-12">
          {isLoading ? (
            <div className="mb-5">
              <LoadingSpinner message="" size={50} label="Loading matches" />
            </div>
          ) : (
            <>
              {displayVendorBoundary && (
                <div className="grid grid-cols-4">
                  <div className="col-span-2 col-start-3">
                    <div className="flex justify-end mb-1 -mt-8">
                      <Button
                        icon={
                          <VisibilityOffOutlined style={{ color: '#696F88' }} />
                        }
                        onClick={handleVendorBoundary}
                        id="hide-vendor-property-boundary-btn"
                        variant="outline"
                        style={{ padding: 0 }}
                      >
                        Hide Boundary
                      </Button>
                    </div>
                    <SelectWithSearch
                      label={`${integrationName} Fields`}
                      onChange={onChangeVendorProperty}
                      options={options}
                      defaultValue={selectedVendorPropertyId}
                    />
                  </div>
                </div>
              )}

              <div
                className={`grid items-${
                  displayVendorBoundary
                    ? 'end grid-cols-3'
                    : 'center grid-cols-4'
                }`}
              >
                <div className="col-span-1 justify-self-start w-full">
                  <div className="mt-2 bg-gray-100 rounded-lg overflow-hidden text-left p-4">
                    <FieldMapView
                      key={`property-preview-with-vendor=${displayVendorBoundary}`}
                      className="rounded-lg"
                      feature={feature}
                      area={property?.reportedArea || property?.boundaryArea}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center h-full col-span-1">
                  <span className="mb-1">
                    <MatchArrowIcon />
                  </span>
                </div>

                <div
                  className={`text-sm col-span-${
                    displayVendorBoundary ? '1' : '2'
                  }`}
                  style={{ position: 'relative' }}
                >
                  {!displayVendorBoundary ? (
                    <>
                      <div className="flex justify-end mb-1 -mt-8">
                        <Button
                          icon={
                            <VisibilityOutlined style={{ color: '#696F88' }} />
                          }
                          onClick={handleVendorBoundary}
                          id="display-vendor-property-boundary-btn"
                          variant="outline"
                          style={{ padding: 0 }}
                        >
                          View Boundary
                        </Button>
                      </div>
                      <div
                        className="text-neutral-600 text-left mb-1 text-sm"
                        data-testid="cropzones-heading"
                      >
                        {integrationName} Crop Zones
                      </div>
                      <SelectWithSearch
                        placeholder="Search by field or crop zone name"
                        onChange={onChangeVendorProperty}
                        options={options}
                        defaultValue={selectedVendorPropertyId}
                      />
                    </>
                  ) : (
                    <div className="mt-2 bg-gray-100 rounded-lg overflow-hidden text-left p-4">
                      <VendorPropertyMapView
                        vendorId={selectedVendorPropertyId}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

PropertyView.propTypes = {
  property: PropTypes.objectOf(PropTypes.any),
  integrationName: PropTypes.string,
  parentName: PropTypes.string
};

export default PropertyView;
